import { useState, useId, useEffect, useRef, useMemo } from 'react';
import { Formik, useFormikContext } from 'formik';
import FormikModal from '@components/Modals/FormikModal';
import { DialogContent, Divider } from '@mui/material';
import { FormValues, ReportItem, SetReportParams } from './types';
import ReportDropdownField from '../Forms/Fields/ReportDropdownField';
import dayjs from 'dayjs';
import FormikModalHeader from '../Components/Modals/FormikModalHeader';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useModalProvider } from '@components/Modals/ModalContext';
import useFetch from '@hooks/useFetch';
import CSREfficiencyForm from './CSREfficiencyForm';
import InvoicedChargesAgingForm from './InvoicedChargesAgingForm';
import OrdersWithoutChargesForm from './OrdersWithoutChargesForm';
import DriverManifestForm from './DriverManifestForm';
import InvoiceForm from './InvoiceForm';
import OpsProfitabilityForm from './OpsProfitabilityForm';
import NotFullyDeliveredForm from './NotFullyDeliveredForm';
import { useFetchMyProfileQuery } from '@api/Trinium/Endpoints/myProfile';

import styles from '../Forms/Components/Form.module.css';

const reports: ReportItem[] = [
  {
    code: 'CSREfficiency',
    name: 'CSR Efficiency'
  },
  {
    code: 'DriverManifest',
    name: 'Driver Manifest'
  },
  {
    code: 'Invoice',
    name: 'Invoice'
  },
  {
    code: 'NotFullyDelivered',
    name: 'Loads Not Fully Delivered'
  },
  {
    code: 'NonInvoicedChargesAging',
    name: 'Non-Invoiced Charges Aging'
  },
  {
    code: 'OpsProfitability',
    name: 'Ops Profitability/Margin By Customer'
  },
  {
    code: 'OrdersWithoutCharges',
    name: 'Orders Without Charges'
  }
];

/**
 * Function to override the default values applied to the form based on the report type selected
 */
function getReportDefaults(reportName: string): Partial<FormValues> {
  switch (reportName) {
    case 'DriverManifest': {
      const today = dayjs();
      const daysBack = (today.day() - 1 + 7) % 7;
      const previousMonday = today.subtract(daysBack, 'day');
      const tuesdayBefore = previousMonday.subtract(6, 'day');
      return {
        reportDateRange: [tuesdayBefore.startOf('day'), previousMonday.endOf('day')]
      };
    }

    case 'Invoice': {
      const today = dayjs();
      const daysBack = (today.day() - 5 + 7) % 7;
      const previousFriday = today.subtract(daysBack, 'day');
      const nextThursday = previousFriday.add(6, 'day');

      const monDaysBack = (today.day() - 1 + 7) % 7;
      const previousMonday = today.subtract(monDaysBack, 'day');

      return {
        reportDateRange: [previousFriday.startOf('day'), nextThursday.endOf('day')],
        deliveryDateRange: [previousMonday.subtract(3, 'month').startOf('day'), previousMonday.endOf('day')]
      };
    }

    case 'NotFullyDelivered':
      return { accept: 'Excel' };

    default:
      return {};
  }
}

/**
 * Renderless component that handles applying default values per report
 */
function ReportDefaultsApplier({ baseValues }: { baseValues: FormValues }) {
  const { values, setValues } = useFormikContext<FormValues>();
  const prevReportName = useRef(values.reportName);

  useEffect(() => {
    if (values.reportName !== prevReportName.current) {
      prevReportName.current = values.reportName;
      const defaults = getReportDefaults(values.reportName);
      void setValues({ ...baseValues, ...defaults, reportName: values.reportName });
    }
  }, [values.reportName, setValues, baseValues]);

  return null;
}

/**
 * Modal component for selecting and generating a report
 */
export default function ReportModal() {
  const [isOpen, setIsOpen] = useState(true);

  const onClose = () => {
    setIsOpen(false);
  };

  const formId = useId();
  const { number: terminalNumber } = useCurrentTerminal();
  const initialValues = useMemo<FormValues>(() => {
    const today = dayjs();
    const firstDay = today.startOf('month');
    return {
      terminalNumber: terminalNumber,
      reportName: '',
      accept: 'HTML',
      cntrProviderId: '',
      reportStyle: 'User Detail',
      reportType: 'Summary',
      dateFilter: 'Delivery Date',
      allCharges: false,
      sortBy: 'Bill to Customer',
      reportDateRange: [firstDay, today],
      deliveryDateRange: [null, null],
      dateType: 'Delivery Date',
      inclFsc: false,
      totalsOnly: false,
      driverPay: 'All',
      jobCategories: [],
      consignees: [],
      services: [],
      customers: []
    };
  }, [terminalNumber]);

  const { data: userProfile } = useFetchMyProfileQuery(undefined);
  // The Loads Not Fully Delivered and OpsProfitability reports are restricted to users holding the specific roles
  const availableReports = useMemo(
    () =>
      reports.filter(
        (report) =>
          (report.code === 'NotFullyDelivered' && userProfile?.canViewNotFullyDelivered) ||
          (report.code === 'OpsProfitability' && userProfile?.canViewProfitPayApproval) ||
          !['NotFullyDelivered', 'OpsProfitability'].includes(report.code)
      ),
    [userProfile]
  );

  const { queueModalAnnouncement } = useModalProvider();
  const { fetchFile, isError, errorMessage, isLoading } = useFetch();

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const handleSubmit = async (values: FormValues) => {
    const params = SetReportParams(values);
    setSubmitAttempted(true);
    await fetchFile(
      `/${terminalNumber}/reports/${values.reportName}?${params.toString()}`,
      'GET',
      undefined, // body
      values.accept === 'Excel' ? 'application/vnd.ms-excel' : 'text/html'
    );
  };
  useEffect(() => {
    if (submitAttempted && !isLoading) {
      if (!isError) {
        setIsOpen(false);
      } else {
        queueModalAnnouncement({
          message: errorMessage,
          severity: 'error'
        });
      }
      setSubmitAttempted(false);
    }
  }, [isLoading, isError, submitAttempted, queueModalAnnouncement, errorMessage]);

  return (
    <div id='modal-root'>
      <FormikModal
        isOpen={isOpen}
        onClose={onClose}>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}>
          {(props) => (
            <>
              <ReportDefaultsApplier baseValues={initialValues} />
              <FormikModalHeader
                title='Reports'
                formName={formId}
                submitButtonText='View'
                submitButtonMessage=''
                onClose={() => {
                  setIsOpen(false);
                }}
              />
              <DialogContent>
                <ReportDropdownField
                  fieldName='reportName'
                  className={styles['flex-20']}
                  reports={availableReports}
                />
                <Divider sx={{ margin: '20px 0' }} />
                {props.values.reportName === 'CSREfficiency' && (
                  <CSREfficiencyForm
                    id={formId}
                    {...props}
                  />
                )}
                {props.values.reportName === 'DriverManifest' && (
                  <DriverManifestForm
                    id={formId}
                    {...props}
                  />
                )}
                {props.values.reportName === 'Invoice' && (
                  <InvoiceForm
                    id={formId}
                    {...props}
                  />
                )}
                {props.values.reportName === 'NotFullyDelivered' && (
                  <NotFullyDeliveredForm
                    id={formId}
                    {...props}
                  />
                )}
                {props.values.reportName === 'NonInvoicedChargesAging' && (
                  <InvoicedChargesAgingForm
                    id={formId}
                    {...props}
                  />
                )}
                {props.values.reportName === 'OpsProfitability' && (
                  <OpsProfitabilityForm
                    id={formId}
                    {...props}
                  />
                )}
                {props.values.reportName === 'OrdersWithoutCharges' && (
                  <OrdersWithoutChargesForm
                    id={formId}
                    {...props}
                  />
                )}
              </DialogContent>
            </>
          )}
        </Formik>
      </FormikModal>
    </div>
  );
}
